import { CartFragment, CartItemFragment } from '@emico-hooks/cart-fragment'
import { useCartId } from '@emico-hooks/cart-id'
import * as React from 'react'

import { useCartItems } from './useCartItems'
import { ConfigurableProduct } from '../../catalog/ProductPage/ConfigurableProduct'
import { Product } from '../../catalog/ProductPage/GetProduct.query'
import useAddToCartFunction from '../../checkout/useAddToCartFunction'
import { useRemoveCartItemFunction } from '../../checkout/useRemoveItemFromCart'
import {
    resolveConfigurableOptions,
    serializeConfigurableOptions,
} from '../CartItem'

type DeletedCartItemData = CartItemFragment & {
    // The old item id before this item was deleted and restored. We control
    // this manually so restored items (which will be newly added items) can
    // maintain their old position while sorting.
    oldKey?: number
}

// This hook enables cart item restoration after items have been removed from
// the backend. It does this without caching the backend response so that can
// still be mutated freely (e.g. to change quantity, sizes, apply price rules,
// add free gifts, etc.).
// The items are sorted by when they were added to the cart, and item sort
// position is maintained for removed or restored items.
// prettier-ignore
const useCartItemRestoration = (): {
    cartItems: CartItemFragment[]|undefined,
    removeItem: (cartItem: CartItemFragment, withRestore?:boolean) => Promise<CartFragment|undefined>,
    restoreItem: (cartItem: CartItemFragment, product: Product) => Promise<void>,
    removeItemFromHistory:(cartItem: CartItemFragment) => void,
    loading:boolean
    hasItems:boolean
    quantity:number
    
} => {
    const cartId = useCartId()
    const {items: cartItems, loading, hasItems, quantity} = useCartItems()
    const [removedItems, setRemovedItems] = React.useState<
        DeletedCartItemData[]
    >([])

    
    const removeCartItem = useRemoveCartItemFunction()
    const addToCart = useAddToCartFunction()

    const handleRemove = async(
        item: CartItemFragment,
        withRestore:boolean=false
        
    ) => {
        
        setRemovedItems(removedItems => [
            ...removedItems,
            {
                ...item,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                oldKey: cartItems
                    ? Number(Object.keys(cartItems).find((key) => cartItems[Number(key)] === item))
                    : undefined
            },
        ])
    
        return removeCartItem(cartId??"",item, withRestore)
    }

    const handleRemoveFromHistory = (item: CartItemFragment) => {
        setRemovedItems(removedItems =>
            removedItems.filter(
                removedItem => removedItem.id !== item.id,
            ),
        )
    }

    const handleRestore = async (
        item: CartItemFragment,
        product: Product,
    ) => {

        const selectedOptions = (product as ConfigurableProduct).configurableOptions && 
        resolveConfigurableOptions(item.configurableOptions.map(option=>({
            label: option.optionLabel,
                value: option.configurableProductOptionValueUid,
        })), product as ConfigurableProduct)

        await addToCart(product, item.quantity, selectedOptions ? serializeConfigurableOptions(selectedOptions) : undefined, false)
        handleRemoveFromHistory(item)
    }

    if (!cartItems && removedItems.length === 0) {
        return {cartItems:[], 
            removeItem: handleRemove, 
            restoreItem: handleRestore, 
            removeItemFromHistory: handleRemoveFromHistory, 
            loading, 
                hasItems,
                quantity

        }
    }

    // Remove items that are deleted or about to be deleted. They will be
    // added back in the statement further below, and they may disappear
    // from this array when they're really deleted.
    const originalItems= (cartItems || [])
        .filter(
            item =>
                !removedItems.some(
                    removedItem => removedItem.id === item?.id,
                ),
        )

    // TypeScript doesn't have proper `instanceof` support, so act like all
    // items are of the same type and oldKey is just some optional prop.
    const allItems = [
        ...originalItems,
        ...removedItems,
    ]

    const sortedItems = allItems.sort((a, b) => {
        const aSortKey = a?.oldKey !== undefined ? a.oldKey - 1 : allItems.indexOf(a)
        const bSortKey = b?.oldKey !== undefined ? b.oldKey - 1 : allItems.indexOf(b)

        return aSortKey - bSortKey
    })

    return {cartItems:sortedItems, removeItem: handleRemove, restoreItem: handleRestore, removeItemFromHistory: handleRemoveFromHistory, loading, hasItems, quantity}
}

export default useCartItemRestoration
